import ChecklistIcon from "@mui/icons-material/Checklist"
import CircleNotificationsIcon from "@mui/icons-material/CircleNotifications"
import CookieIcon from "@mui/icons-material/Cookie"
import DarkModeIcon from "@mui/icons-material/DarkMode"
import EditNoteIcon from "@mui/icons-material/EditNote"
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"
import LightModeIcon from "@mui/icons-material/LightMode"
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip"
import ViewSidebarIcon from "@mui/icons-material/ViewSidebar"
import { ReactElement } from "react"
import { useNavigate } from "react-router-dom"

import {
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    DNO_ADMIN,
    DNO_APPROVED,
    DNO_REVIEWER,
    ENA_SYS_ADMIN,
    INSTALLER,
    INSTALLER_ADMIN,
    LCT_MANU_ADMIN,
    LCT_MANU_APPROVED,
} from "../../constants/roles"
import { useAppDispatch } from "../../hooks/useDispatch"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    DARK,
    LIGHT,
    getSideBar,
    getTheme,
    setSideBar,
    setTheme,
} from "../../redux/preferencesSlice"
import {
    ADMIN_PORTAL,
    COOKIE_POLICY,
    DNO_APPLICATIONS_ESCALATIONS,
    DNO_APPLICATIONS_NOTIFICATIONS,
    LEARN_MORE,
    GEN_DEVICE_SEARCH,
    INSTALLER_LIST_APPLICATIONS,
    NEW_SINGLE_APPLICATION,
    PRIVACY_POLICY,
    TERMS_OF_USE,
    INSTALLER_DRAFTS,
} from "../../router/routes"
import {
    AdminIcon,
    ApprovalsIcon,
    DatabaseIcon,
    EVChargerIcon,
} from "../icons/icons"

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore: Unreachable code error
// eslint-disable-next-line no-undef
const CONFIG = GLOBAL_CONFIG

const MAX_ITEMS = 5

interface IQuickAction {
    title: string
    icon: ReactElement
    onClick: () => void
    displayRoles?: string[]
}

/**
 * Generates filtered list of quick actions based on the search term.
 * Pre filters actions based on user role. Only returns 5 items at a time.
 * @param search - search term
 * @returns {IQuickAction[]} list of quick actions
 */
export const useGetQuickActions = (search: string): IQuickAction[] => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { groups } = useGetUserData()
    const uiTheme = useGetState(getTheme)
    const sideBar = useGetState(getSideBar)

    const quickActions = [
        {
            title: "Raise a support ticket",
            icon: <HelpOutlineIcon />,
            onClick: () => window.open(CONFIG.SN_PORTAL_URL, "_blank"),
            displayRoles: [
                ENA_SYS_ADMIN,
                INSTALLER,
                LCT_MANU_APPROVED,
                DNO_APPROVED,
                LCT_MANU_APPROVED,
                DB_ASSESSOR_DEMAND,
                DB_ASSESSOR_GEN,
            ],
        },
        {
            title: "Start a new application",
            icon: <EVChargerIcon />,
            onClick: () => navigate(NEW_SINGLE_APPLICATION),
            displayRoles: [INSTALLER],
        },
        {
            title: "View My Applications",
            icon: <ChecklistIcon />,
            onClick: () => navigate(INSTALLER_LIST_APPLICATIONS),
            displayRoles: [INSTALLER],
        },
        {
            title: "My Drafts",
            icon: <EditNoteIcon />,
            onClick: () => navigate(INSTALLER_DRAFTS),
            displayRoles: [INSTALLER],
        },
        {
            title: "View Applications Awaiting Review",
            icon: <ApprovalsIcon />,
            onClick: () => navigate(DNO_APPLICATIONS_ESCALATIONS),
            displayRoles: [DNO_REVIEWER],
        },
        {
            title: "View Resolved Applications",
            icon: <CircleNotificationsIcon />,
            onClick: () => navigate(DNO_APPLICATIONS_NOTIFICATIONS),
            displayRoles: [DNO_REVIEWER],
        },
        {
            title: "Switch Theme",
            icon: uiTheme === DARK ? <LightModeIcon /> : <DarkModeIcon />,
            onClick: () => dispatch(setTheme(uiTheme === DARK ? LIGHT : DARK)),
        },
        {
            title: "Flip Navigation Bar",
            icon: <ViewSidebarIcon />,
            onClick: () => dispatch(setSideBar(!sideBar)),
        },
        {
            title: "Go To Asset Registers",
            icon: <DatabaseIcon />,
            onClick: () => navigate(GEN_DEVICE_SEARCH),
        },
        {
            title: "Manage Users",
            icon: <AdminIcon />,
            onClick: () => navigate(ADMIN_PORTAL),
            displayRoles: [
                ENA_SYS_ADMIN,
                INSTALLER_ADMIN,
                LCT_MANU_ADMIN,
                DNO_ADMIN,
            ],
        },
        {
            title: "Privacy Policy",
            icon: <PrivacyTipIcon />,
            onClick: () => navigate(PRIVACY_POLICY),
        },
        {
            title: "Cookie Policy",
            icon: <CookieIcon />,
            onClick: () => navigate(COOKIE_POLICY),
        },
        {
            title: "Terms of Use",
            icon: <ChecklistIcon />,
            onClick: () => navigate(TERMS_OF_USE),
        },
        {
            title: "Go to FAQs",
            icon: <HelpOutlineIcon />,
            onClick: () => navigate(LEARN_MORE),
            displayRoles: [
                ENA_SYS_ADMIN,
                INSTALLER,
                LCT_MANU_APPROVED,
                DNO_APPROVED,
                LCT_MANU_APPROVED,
                DB_ASSESSOR_DEMAND,
                DB_ASSESSOR_GEN,
            ],
        },
    ]

    const filteredActions = quickActions.filter((action) => {
        if (!action.displayRoles) return true
        return action.displayRoles.some((role) => groups.includes(role))
    })

    // return only actions where the search term is contained in the title (case insensitive)
    if (search) {
        return filteredActions
            .filter((action) =>
                action.title.toLowerCase().includes(search.toLowerCase())
            )
            .slice(0, MAX_ITEMS)
    }

    return filteredActions.slice(0, MAX_ITEMS)
}
